import { useContext, createContext } from 'react';
import { runInAction, makeAutoObservable } from 'mobx';
import { RootStore, getRootStoreInstance } from 'store/rootStore';
import { completeSubscriberChecklistStep, getRequiredSubInfo, getSubscriberById } from 'api/operations/electionService';
import { bpHistory } from 'global/bpHistory';
import { getSubscriberTaskChecklistURL } from 'global/checklists-helpers/SubscriberChecklistHelper';
import { getGroupById } from 'api/operations/groupService';
import { Group } from 'types/groupService';
import { ChecklistOption } from 'types/enrollmentService';
import { SubChecklistCard } from 'types/electionService';
import SubscriberDependentDelegate from 'store/modules/elections/dependents/subDependentDelegate';
import SubscriberDelegate from 'store/modules/elections/subscriberDelegate';
import { AddDependentModalStore } from 'store/modules/elections/dependents/addDependentModalStore';
import { RemoveDependentModalStore } from 'store/modules/elections/dependents/removeDependentModalStore';
import { UpdateDependentModalStore } from 'store/modules/elections/dependents/updateDependentModalStore';

interface DomainData {
  subscriberId: string;
  // TaskId
  entityId: string;
  groupId: string;
}

export default class SubscriberDependentsPageStore {
  private _rootStore: RootStore;
  private _domainData: DomainData;
  private _isLoading = false;
  private _group: Pick<Group, 'name' | 'id' | 'contactInfo'> | null = null;
  private _manageDependentFormId = '';
  public subscriberDelegate: SubscriberDelegate | null = null;
  public addDependentModalStore: AddDependentModalStore | null = null;
  public removeDependentModalStore: RemoveDependentModalStore | null = null;
  public updateDependentModalStore: UpdateDependentModalStore | null = null;

  public get isLoading(): boolean {
    return this._isLoading;
  }

  public get domainData(): DomainData {
    return this._domainData;
  }

  public get checklistOptions(): ChecklistOption<SubChecklistCard>[] {
    return this.subscriberDelegate?.getTaskChecklistOptions(this._domainData.entityId) || [];
  }

  public get group(): Pick<Group, 'name' | 'id' | 'contactInfo'> | null {
    return this._group;
  }

  public get formId(): string {
    return this._manageDependentFormId;
  }

  public get dependentDelegates(): SubscriberDependentDelegate[] {
    return this.subscriberDelegate?.subscriberDependentsDelegates || [];
  }

  private _setLoading(loadingStatus: boolean): void {
    this._isLoading = loadingStatus;
  }

  private async _getSubscriber(): Promise<void> {
    const subscriber = await getSubscriberById(this._domainData.subscriberId);
    runInAction(() => {
      this.subscriberDelegate = new SubscriberDelegate({ subscriber, periodId: this._domainData.entityId });
      this.addDependentModalStore = new AddDependentModalStore({
        subscriberDelegate: this.subscriberDelegate,
        formId: this._manageDependentFormId,
      });
    });
  }

  private _setGroup(group: Group): void {
    this._group = {
      name: group.name,
      id: group.id,
      contactInfo: group.contactInfo,
    };
  }

  public async getInitialData(): Promise<void> {
    this._setLoading(true);
    const [group] = await Promise.all([getGroupById(this._domainData.groupId), this._getRequiredSubInfo()]);
    await this._getSubscriber();
    runInAction(() => {
      this._setGroup(group);
      this._setLoading(false);
    });
  }

  private async _getRequiredSubInfo(): Promise<void> {
    const domainId = this._rootStore.domainStoreV2.domainHeader.id;
    const requiredSubInfo = await getRequiredSubInfo(domainId);
    runInAction(() => {
      this._manageDependentFormId = requiredSubInfo?.manageDependentFormId || '';
    });
  }

  public async completeChecklistStep(): Promise<void> {
    await completeSubscriberChecklistStep({
      subId: this._domainData.subscriberId,
      entityId: this._domainData.entityId,
      card: SubChecklistCard.SUBSCRIBER_DEPENDENTS,
    });
    this.returnBack();
  }

  public returnBack(): void {
    bpHistory.push(
      getSubscriberTaskChecklistURL({
        entityId: this._domainData.entityId,
        subId: this._domainData.subscriberId,
        groupId: this._domainData.groupId,
      }),
    );
  }

  constructor(domainData: DomainData) {
    this._domainData = domainData;
    this._rootStore = getRootStoreInstance();

    makeAutoObservable(this, {}, { autoBind: true });
  }
}

const SubscriberDependentsPageStoreContext = createContext(
  new SubscriberDependentsPageStore({
    groupId: 'stub-group-id',
    subscriberId: 'stub-subscriber-id',
    entityId: 'stub-entity-id',
  }),
);

export const useSubscriberDependentsPageStore = (): SubscriberDependentsPageStore => {
  return useContext(SubscriberDependentsPageStoreContext);
};

export { SubscriberDependentsPageStoreContext };
